/* eslint-disable no-console */
import {Flex} from 'antd';
import React, {useEffect, useMemo, useState} from 'react';

import {sumEmissionsOffset} from '../../../../../components/Forms/Constants/CarbonOffset/CarbonOffsetKeyLabel';
import type {CarbonOffsetRow} from '../../../../../components/Table/CarbonOffsetTable';
import {subscriptionPlanTypes} from '../../../../../constants/subscriptionConstants';
import {APITypeEnum} from '../../../../../services/interface/tableAPIType';
import carbonOffsetAPI from '../../../../../services/modules/OM/carbonOffsetAPI';
import useCompanyStore from '../../../../../store/companyStore/companyDetailsStore';
import {formatRoundedIndian} from '../../../../../utils/formatIndian';
import {getSubscriptionPlanType} from '../../../../../utils/SubscriptionHelper';
import {statusEm} from '../../../../netZero/utils/commonFunctions';
import {categoryWiseLabels} from '../constants/commonConstants';

import BenchmarkCard from './BenchmarkCard';
import {
  aboveBenchmarkTags,
  buildBenchmarkCards,
  totalEmissionsFromScope
} from './utils';

type BenchmarkCategory = keyof typeof categoryWiseLabels;

const CARDS_PER_ROW = 3;
const CATEGORY_KEYS = Object.keys(categoryWiseLabels) as BenchmarkCategory[];
const CATEGORY_SUBTITLES: Record<BenchmarkCategory, string> = {
  scope: 'GHG emissions by scope compared with the sector benchmark',
  intensity: 'GHG intensity by scope compared with the sector benchmark',
  consumption: 'Total consumption compared with the sector benchmark'
};
const FREE_PLAN_LOCKED_CARD_KEYS = new Set([
  'scope 3 ghg emissions',
  'scope 3 ghg intensity',
  'total energy consumption',
  'Total quantity of waste generated'
]);

const formatTotal = (value: number | null): string =>
  value === null ? '-' : formatRoundedIndian(value);

const IndustryBenchmarks: React.FC<{
  emissionsData: any;
  intensityData: any;
  consumptionData: any;
  showTotalEmissions?: boolean;
  showSummaryLine?: boolean;
  selectedYear?: string;
}> = ({
  emissionsData,
  intensityData,
  consumptionData,
  showTotalEmissions = true,
  showSummaryLine = false,
  selectedYear
}) => {
  const [activeCategory, setActiveCategory] =
    useState<BenchmarkCategory>('scope');

  const allCards = useMemo(
    () => buildBenchmarkCards(emissionsData, intensityData, consumptionData),
    [emissionsData, intensityData, consumptionData]
  );

  const totalEmissions = totalEmissionsFromScope(emissionsData);
  const isFreePlan = getSubscriptionPlanType() === subscriptionPlanTypes.FREE;

  const companyId = useCompanyStore((state) => state.companyId);

  // Sum of Accepted carbon offset records for the year, matching the Carbon
  // Offset page. Null when there are no records or they could not be loaded.
  const [totalEmissionOffset, setTotalEmissionOffset] = useState<number | null>(null);

  useEffect(() => {
    setTotalEmissionOffset(null);

    if (!selectedYear || !companyId) {
      return;
    }

    let isActive = true;

    carbonOffsetAPI(APITypeEnum.GET_ALL, undefined, `${companyId}/${selectedYear}`)
      .then((rows: CarbonOffsetRow[] | undefined) => {
        if (isActive) {
          const acceptedRows = (rows ?? []).filter((row) => {
            return row.status === statusEm.ACCEPTED;
          });

          setTotalEmissionOffset(
            rows?.length ? sumEmissionsOffset(acceptedRows) : null
          );
        }
      })
      .catch(() => {
        if (isActive) {
          setTotalEmissionOffset(null);
        }
      });

    // eslint-disable-next-line consistent-return
    return () => {
      isActive = false;
    };
  }, [companyId, selectedYear]);

  const isCardLockedForFreePlan = (card: any): boolean => {
    if (!isFreePlan) return false;
    const cardId = `${card?.id ?? ''}`.trim().toLowerCase();
    const cardLabel = `${card?.label ?? ''}`.trim().toLowerCase();

    return (
      FREE_PLAN_LOCKED_CARD_KEYS.has(cardId) ||
      FREE_PLAN_LOCKED_CARD_KEYS.has(cardLabel)
    );
  };

  const aboveBenchmark = aboveBenchmarkTags(
    allCards.filter((card) => !isCardLockedForFreePlan(card))
  );

  const activeCards = allCards.filter(
    (card) => card.category === activeCategory
  );

  // With no carbon offset, net emissions equal total emissions.
  const netEmissions = totalEmissions - (totalEmissionOffset ?? 0);

  return (
    <div className="industryBenchmarksSection">
      <span className="industryBenchmarksTitle">
        Industry Benchmarks vs Company
      </span>
      <Flex className="industryBenchmarksLayout" gap={16}>
        {showTotalEmissions && (
          <Flex className="industryBenchmarksSummary" vertical gap={16}>
            <Flex vertical gap={8}>
              <span className="industryBenchmarksTotalLabel industryBenchmarksTotalLabel--primary">
                Total Emissions
              </span>
              <Flex className="industryBenchmarksTotalValueRow" gap={12}>
                <span className="industryBenchmarksTotalValue">
                  {formatTotal(totalEmissions)}
                </span>
                <span className="industryBenchmarksTotalUnit">tCO2e</span>
              </Flex>
            </Flex>
            <div className="industryBenchmarksDivider" />
            <Flex vertical gap={12}>
              <span className="industryBenchmarksTotalLabel">
                Total Emission Offset
              </span>
              <Flex className="industryBenchmarksTotalValueRow" gap={10}>
                <span className="industryBenchmarksTotalValue">
                  {formatTotal(totalEmissionOffset)}
                </span>
                <span className="industryBenchmarksTotalUnit">tCO2e</span>
              </Flex>
            </Flex>
            <div className="industryBenchmarksDivider" />
            <Flex vertical gap={12}>
              <span className="industryBenchmarksTotalLabel">
                Net Emissions
              </span>
              <Flex className="industryBenchmarksTotalValueRow" gap={10}>
                <span className="industryBenchmarksTotalValue industryBenchmarksTotalValue--net">
                  {formatTotal(netEmissions)}
                </span>
                <span className="industryBenchmarksTotalUnit">tCO2e</span>
              </Flex>
            </Flex>
            {aboveBenchmark.length > 0 && (
              <>
                <div className="industryBenchmarksDivider" />
                <Flex vertical gap={10}>
                  <span className="industryBenchmarksAboveLabel">
                    {`⚠ ${aboveBenchmark.length} ${
                      aboveBenchmark.length === 1 ? 'indicator' : 'indicators'
                    } above benchmark`}
                  </span>
                  <Flex wrap="wrap" gap={6}>
                    {aboveBenchmark.map((card) => (
                      <button
                        key={card.id}
                        type="button"
                        className="industryBenchmarkTag"
                        onClick={() => {
                          setActiveCategory(card.category);
                        }}
                      >
                        {`• ${card.label}`}
                      </button>
                    ))}
                  </Flex>
                </Flex>
              </>
            )}
          </Flex>
        )}

        <Flex className="industryBenchmarksContent" vertical gap={16}>
          <Flex className="industryBenchmarksToggle" gap={2} align="center">
            {CATEGORY_KEYS.map((category) => (
              <button
                key={category}
                type="button"
                className={`industryBenchmarksToggleTab ${
                  activeCategory === category ? 'active' : ''
                }`}
                onClick={() => {
                  setActiveCategory(category);
                }}
              >
                {categoryWiseLabels[category]}
              </button>
            ))}
          </Flex>
          <span className="industryBenchmarksSubtitle">
            {CATEGORY_SUBTITLES[activeCategory]}
          </span>
          {activeCards.length > 0 && (
            <Flex className="industryBenchmarksCards" gap={16}>
              {activeCards.map((item) => (
                <div key={item.id} className="industryBenchmarkCardWrap">
                  <BenchmarkCard
                    item={item}
                    isBlocked={isCardLockedForFreePlan(item)}
                    showSummaryLine={showSummaryLine}
                  />
                </div>
              ))}
              {activeCards.length < CARDS_PER_ROW &&
                Array.from({
                  length: CARDS_PER_ROW - activeCards.length
                }).map((_, i) => (
                  <div
                    key={`empty-${i}`}
                    className="industryBenchmarkCardWrap industryBenchmarkCardEmpty"
                  />
                ))}
            </Flex>
          )}
        </Flex>
      </Flex>
    </div>
  );
};

export default IndustryBenchmarks;
